import React, {
  useContext, useEffect, useRef, useState,
} from 'react';

import { MDText } from 'i18n-react';

import { getSitemap } from 'PublicAPI';

import { ConfigContext } from 'Context/config';

import { useFeatureFlags } from 'Utilities/hooks';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import { Expandable } from '../../../components/expandable/Expandable';

import SitemapSection from './SitemapSection/SitemapSection';
import LocalTexts from './sitemap.texts.json';

import styles from './Sitemap.scss';

const LocalT = new MDText(LocalTexts);

export const getBlogDates = (sitemapBlogData = []) => sitemapBlogData?.map((item) => {
  const year = new Date(item.date).getFullYear();
  return Number.isNaN(year) ? null : year;
}).filter(Boolean);

const Sitemap = () => {
  const { configActions: { update: updateConfig }, configState: { sitemapData } } = useContext(ConfigContext);
  const [loading, setLoading] = useState(!sitemapData);
  const { showWordpressGuideAuthorsPage } = useFeatureFlags();

  const { current: update } = useRef(updateConfig);

  useEffect(() => {
    if (loading) {
      getSitemap()
        .then((data) => {
          update({ sitemapData: data });
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          window?.Sentry?.captureException(new Error(err));
        });
    }
  }, [loading, update]);

  if (loading) {
    return (
      <ComponentContent maxWidth={CONTENT_SIZES.container}>
        <div className={styles.loading}>
          <LoadingArrow />
        </div>
      </ComponentContent>
    );
  }

  const blogDates = getBlogDates(sitemapData?.blog);
  const uniqueBlogDates = [...new Set(blogDates)];

  const charsAtoM = new RegExp(`^${LocalT.translate('prefix')} [A-M]`, 'i');
  const charsNtoZ = new RegExp(`^${LocalT.translate('prefix')} [N-Z]`, 'i');

  return (
    <ComponentContent className={styles.component} maxWidth={CONTENT_SIZES.container}>
      <div className={styles.heading}>
        <h1>{LocalT.translate('headings.title')}</h1>
      </div>
      <div className={styles.common}>
        <SitemapSection data={sitemapData?.sell} header={LocalT.translate('headings.header.sell')} />
        <SitemapSection data={sitemapData?.misc} header={LocalT.translate('headings.header.misc')} />
      </div>
      <div className={styles.heading}>
        <h2>{LocalT.translate('headings.header.makes')}</h2>
        <hr />
      </div>
      <div className={styles.expandableBrands}>
        <Expandable
          key="make-1"
          caption="A-M"
          className={styles.expandable}
          id="make-1"
        >
          <SitemapSection
            data={sitemapData?.makes
              ?.map((item) => ({ ...item, title: `${LocalT.translate('prefix')} ${item.title}` }))
              ?.sort((a, b) => a.title.localeCompare(b.title))
              ?.filter(({ title }) => charsAtoM.test(title))}
          />
        </Expandable>
        <Expandable
          key="make-2"
          caption="N-Z"
          className={styles.expandable}
          id="make-2"
        >
          <SitemapSection
            data={sitemapData?.makes
              ?.map((item) => ({ ...item, title: `${LocalT.translate('prefix')} ${item.title}` }))
              ?.sort((a, b) => a.title.localeCompare(b.title))
              ?.filter(({ title }) => charsNtoZ.test(title))}
          />
        </Expandable>
      </div>
      <div className={styles.guides}>
        <SitemapSection
          data={sitemapData?.guides?.slice(0, 16)}
          header={LocalT.translate('headings.header.guides')}
        />
        <div className={styles.expandableGuides}>
          <Expandable
            key="guides"
            caption="View all guides"
            className={styles.expandable}
            id="guides"
          >
            <SitemapSection
              data={sitemapData?.guides?.slice(16)}
            />
          </Expandable>
        </div>
      </div>
      <div className={styles.heading}>
        <h2>{LocalT.translate('headings.header.blog')}</h2>
        <h3>{LocalT.translate('headings.subtitle')}</h3>
        <hr />
      </div>
      <div className={styles.expandableBlogs}>
        {uniqueBlogDates?.map((item, index) => (
          <Expandable
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            caption={item.toString()}
            className={styles.expandable}
            id={item.toString()}
          >
            <SitemapSection
              data={sitemapData?.blog?.filter((itemDate) => new Date(itemDate.date).getFullYear() === item)}
            />
          </Expandable>
        ))}
        {showWordpressGuideAuthorsPage && (
          <Expandable
            key="authors"
            caption="Authors"
            className={styles.expandable}
            id="authors"
          >
            <SitemapSection
              data={sitemapData?.authors?.filter((item) => item.link?.startsWith('/guides/authors/'))}
            />
          </Expandable>
        )}
      </div>
    </ComponentContent>
  );
};

export default Sitemap;
